import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { advertisers } from "../api/advertisers";
import { ApiError } from "../api/client";

export function AdvertiserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [domain, setDomain] = useState("");
  const [domainMessage, setDomainMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(null);
    setMessage(null);
    setDomain("");
    setDomainMessage(null);
  }, [id]);

  const detail = useQuery({
    queryKey: ["advertiser", id],
    queryFn: () => advertisers.get(id!),
    enabled: Boolean(id),
  });

  const audit = useQuery({
    queryKey: ["advertiser", id, "audit"],
    queryFn: () => advertisers.audit(id!),
    enabled: Boolean(id),
  });

  const actionUrlDomains = useQuery({
    queryKey: ["advertiser", id, "action-url-domains"],
    queryFn: () => advertisers.actionUrlDomains(id!),
    enabled: Boolean(id),
  });

  const addActionUrlDomain = useMutation({
    mutationFn: (hostnamePattern: string) => advertisers.addActionUrlDomain(id!, {
      hostnamePattern,
      purpose: "Переход из рекламного креатива",
      rationale: "Домен разрешён администратором рекламодателя",
      redirectorClassification: "not_redirector",
    }),
    onSuccess: async () => {
      setDomain("");
      setDomainMessage("Домен добавлен");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["advertiser", id, "action-url-domains"] }),
        queryClient.invalidateQueries({ queryKey: ["advertiser", id, "audit"] }),
      ]);
    },
    onError: (error) => {
      setDomainMessage(error instanceof ApiError ? `Не добавлено: ${error.code}` : "Не удалось добавить домен");
    },
  });

  const save = useMutation({
    mutationFn: (displayName: string) => advertisers.update(id!, {
      displayName,
      legalName: detail.data!.legalName,
      legalMetadata: JSON.stringify(detail.data!.legalMetadata),
    }),
    onSuccess: async () => {
      setMessage("Сохранено");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["advertiser", id] }),
        queryClient.invalidateQueries({ queryKey: ["advertisers"] }),
        queryClient.invalidateQueries({ queryKey: ["advertiser", id, "audit"] }),
      ]);
    },
    onError: (error) => {
      setMessage(error instanceof ApiError ? `Не сохранено: ${error.code}` : "Не сохранено");
    },
  });

  if (!id) return <p className="error">Не указан идентификатор.</p>;
  if (detail.isLoading) return <p className="muted">Загружаю…</p>;
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return <p className="error">Рекламодатель не найден.</p>;
    }
    return (
      <p className="error" role="alert">
        Не удалось загрузить рекламодателя.{" "}
        <button type="button" onClick={() => void detail.refetch()}>Повторить</button>
      </p>
    );
  }
  if (!detail.data) return null;

  const value = draft ?? detail.data.displayName;

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setMessage(null);
    save.mutate(trimmed);
  }

  return (
    <>
      <p className="muted">
        <Link to="/advertisers">← Рекламодатели</Link>
      </p>
      <h2>{detail.data.displayName}</h2>

      <form className="panel" onSubmit={submit} style={{ marginBottom: 16 }}>
        <div className="field">
          <label htmlFor="name">Название</label>
          <input id="name" value={value} onChange={(e) => setDraft(e.target.value)} />
        </div>
        <p className="muted">
          Статус: {detail.data.status} · версия {detail.data.version}
        </p>
        {message && <p className={message === "Сохранено" ? "muted" : "error"}>{message}</p>}
        <button className="primary" type="submit" disabled={save.isPending || value.trim() === detail.data.displayName}>
          {save.isPending ? "Сохраняю…" : "Сохранить"}
        </button>
      </form>

      <section className="panel" style={{ marginBottom: 16 }}>
        <h3>Разрешённые домены переходов</h3>
        <p className="muted">
          URL можно сохранить в черновике без предварительной настройки. Перед публикацией домен перехода
          должен быть в этом списке. Укажите точный домен или маску одного поддомена, например <code>example.com</code> или <code>*.example.com</code>.
        </p>
        <form className="domain-form" onSubmit={(event) => {
          event.preventDefault();
          const value = domain.trim();
          if (!value) return;
          setDomainMessage(null);
          addActionUrlDomain.mutate(value);
        }}>
          <div className="field">
            <label htmlFor="action-url-domain">Домен</label>
            <input id="action-url-domain" value={domain} placeholder="example.com" onChange={(event) => setDomain(event.target.value)} />
          </div>
          <button className="primary" type="submit" disabled={!domain.trim() || addActionUrlDomain.isPending}>
            {addActionUrlDomain.isPending ? "Добавляю…" : "Добавить домен"}
          </button>
        </form>
        {domainMessage && <p className={domainMessage === "Домен добавлен" ? "notice" : "error"} role="status">{domainMessage}</p>}
        {actionUrlDomains.isLoading && <p className="muted">Загружаю домены…</p>}
        {actionUrlDomains.isError && <p className="error" role="alert">Не удалось загрузить домены.</p>}
        {actionUrlDomains.data?.length === 0 && <p className="muted">Разрешённых доменов пока нет.</p>}
        {actionUrlDomains.data && actionUrlDomains.data.length > 0 && <table>
          <thead><tr><th>Домен</th><th>Добавлен</th></tr></thead>
          <tbody>{actionUrlDomains.data.map((entry) => <tr key={entry.id}>
            <td><code>{entry.hostnamePattern}</code></td>
            <td>{new Date(entry.createdAt).toLocaleString("ru-RU")}</td>
          </tr>)}</tbody>
        </table>}
      </section>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Аудит</h3>
        {audit.isLoading && <p className="muted">Загружаю…</p>}
        {audit.isError && (
          <p className="error" role="alert">
            Не удалось загрузить аудит.{" "}
            <button type="button" onClick={() => void audit.refetch()}>Повторить</button>
          </p>
        )}
        {audit.data && audit.data.length === 0 && <p className="muted">Событий нет.</p>}
        {audit.data && audit.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Действие</th>
                <th>Автор</th>
                <th>Когда</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.map((e) => (
                <tr key={e.id}>
                  <td>{e.operation}</td>
                  <td>admin</td>
                  <td>{new Date(e.occurredAt).toLocaleString("ru-RU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
